<template>
    <div class="pwa-update-toast" v-if="needRefresh && !dismissed">
        <button @click.prevent="dismiss" class="close" aria-label="Dismiss">
            <SvgsClose />
        </button>
        <div>
            <h2 class="turquoise-alert-headline">A new version of this page is available.</h2>
            <p>Update now to refresh the page and load the latest sites, events and fixes.</p>
            <button class="button" @click.prevent="update" :disabled="updating">
                {{ updating ? 'Updating…' : 'Update' }}
            </button>
        </div>
    </div>
</template>

<script setup>
    // Reads the same 'pwa-need-refresh' / 'pwa-update-fn' useState keys
    // app/plugins/07.pwa.client.ts writes to. That plugin no-ops entirely when
    // NUXT_PUBLIC_PWA_DISABLED is set, so needRefresh simply never becomes
    // true in that mode and this component never renders anything.
    const needRefresh = useState('pwa-need-refresh', () => false);
    const updateServiceWorkerFn = useState('pwa-update-fn', () => null);
    const dismissed = ref(false);
    const updating = ref(false);

    // How long to let the service worker activate before reloading anyway.
    // A successful update navigates away well before this — the one run we
    // captured on staging went click -> `controlling` -> reload in about a
    // second and a half — so in the normal case the timer is torn down with
    // the page and never fires. It is deliberately not tighter than that:
    // reloading while the worker is still activating would leave it waiting
    // and simply show this toast again.
    const ACTIVATION_TIMEOUT_MS = 5000;

    let fallbackTimer = null;

    function dismiss() {
        dismissed.value = true;
    }

    // Reported as "doesn't always work on first try" (Asana O2-80), which we
    // could not reproduce — a click on staging updated on the first press. But
    // the path has two places where it can do nothing at all, silently, and no
    // way for anyone to tell that apart from "it is working, wait a moment":
    //
    //   1. updateServiceWorker()'s reloadPage argument is ignored. Passing
    //      `true` reads as "reload the page" and does not do that; see
    //      vite-plugin-pwa/dist/client/build/register.js, where the parameter
    //      is `_reloadPage` and never referenced. The reload comes only from
    //      workbox's `controlling` listener.
    //   2. wb.messageSkipWaiting() is a documented no-op when nothing is
    //      waiting ("If there is no current registration or no service worker
    //      is `waiting`, calling this will have no effect" —
    //      workbox-window/Workbox.js). Nothing activates, so `controlling`
    //      never fires and the reload never happens.
    //
    // So the button now always ends in a reload, and says so while it works.
    async function update() {
        if (updating.value) return;
        updating.value = true;

        const registration = await navigator.serviceWorker
            ?.getRegistration()
            .catch(() => null);

        // Nothing waiting, or the plugin never handed us its update function:
        // the skip-waiting message would go nowhere. Reload directly, which is
        // the right move anyway — with no worker waiting there is nothing to
        // activate and a plain reload picks up whatever is current.
        if (!registration?.waiting || !updateServiceWorkerFn.value) {
            window.location.reload();
            return;
        }

        fallbackTimer = setTimeout(() => window.location.reload(), ACTIVATION_TIMEOUT_MS);
        updateServiceWorkerFn.value(true);
    }

    onBeforeUnmount(() => clearTimeout(fallbackTimer));
</script>

<style lang="scss" scoped>
    .pwa-update-toast {
        z-index: $zStepFour;
        position: fixed;
        bottom: 15px;
        right: 15px;
        left: 15px;
        padding: 30px;
        background-color: $turquoise;
        color: white;
        text-align: center;

        .turquoise-alert-headline {
            margin-bottom: 10px;
            font-weight: 400;
        }

        p {
            margin-bottom: 16px;
        }

        .close {
            position: absolute;
            top: 10px;
            right: 15px;
        }

        .button {
            display: inline-block;
            background-color: white;
            color: $turquoise;

            &:hover {
                background-color: $gray-light;
            }

            // The press needs to read as acknowledged straight away. The
            // update takes a beat to activate the worker and navigate, and
            // with the button unchanged that gap looks like a dead click.
            &:disabled {
                opacity: 0.7;
                cursor: default;

                &:hover {
                    background-color: white;
                }
            }
        }

        @media (min-width: $tablet) {
            max-width: 420px;
            left: auto;
            margin: 0;
        }
    }
</style>
